import React, { useEffect, useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Snackbar, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { jsPDF } from 'jspdf';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useIsMobile } from '../../hooks/useResponsive';
import { ticketsApi } from '../../services/apiClient';

// Components
import TicketListView from './components/TicketListView';
import TicketDetailView from './components/TicketDetailView';
import TicketCreateView from '../../components/Tickets/CreateForm';
import ShortcutsDialog from './components/shared/ShortcutsDialog';

// Hooks
import { useTicketFilters } from './hooks/useTicketFilters';
import { useTickets } from './hooks/useTickets';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useAutoRefresh } from './hooks/useAutoRefresh';

// Utils
import { transformTicketData } from './utils/ticketTransformers';

/**
 * Main Tickets Management Page
 * Orchestrates list, create, view, and edit modes
 */
const TicketsManagement = () => {
  const { t } = useThemeSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile();
  const { hasPermission } = usePermissions();

  // View mode state
  const [viewMode, setViewMode] = useState('list');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedTicketIndex, setSelectedTicketIndex] = useState(-1);

  // Get filter state from hook
  const {
    filter,
    page,
    rowsPerPage,
    orderBy,
    order,
    searchInputValue,
    hasActiveFilters,
    handleFilterChange,
    handleResetFilter,
    handleSearchInputChange,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage
  } = useTicketFilters();

  // Get tickets data from hook
  const {
    tickets,
    totalTickets,
    loading,
    error,
    locations,
    locationsForTreeSelect,
    supportTypes,
    fetchTickets,
    fetchDevices
  } = useTickets({ filter, page, rowsPerPage, orderBy, order });

  // Auto-refresh in list mode
  const { hasNewData, isRefreshing, acceptNewData, dismissNewData } = useAutoRefresh(
    fetchTickets,
    { enabled: viewMode === 'list', interval: 60000 } // Refresh every 60 seconds
  );

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onCreateNew: () => {
      if (viewMode === 'list' && hasPermission('tickets.create')) {
        handleCreateTicket();
      }
    },
    onSearch: () => {
      if (viewMode === 'list') {
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    },
    onRefresh: () => {
      if (viewMode === 'list') {
        fetchTickets();
        enqueueSnackbar(t ? t('Tickets.refreshed') : 'Tickets refreshed', { variant: 'info' });
      }
    },
    onEscape: () => {
      if (shortcutsDialogOpen) {
        setShortcutsDialogOpen(false);
      } else if (filterDrawerOpen) {
        setFilterDrawerOpen(false);
      } else if (viewMode !== 'list') {
        handleBackToList();
      } else {
        // Clear search if in list mode
        if (searchInputValue) {
          handleSearchInputChange('');
        }
      }
    },
    onShowShortcuts: () => {
      setShortcutsDialogOpen(true);
    },
    onOpenFilters: () => {
      if (viewMode === 'list') {
        setFilterDrawerOpen(prev => !prev);
      }
    },
    onEdit: () => {
      if (viewMode === 'view' && selectedTicket) {
        handleEditTicket(selectedTicket);
      }
    },
    onExportPdf: () => {
      if (viewMode === 'list') {
        handleExportPdf();
      }
    },
    onNextTicket: () => {
      if (viewMode === 'list' && tickets.length > 0) {
        const newIndex = Math.min(selectedTicketIndex + 1, tickets.length - 1);
        setSelectedTicketIndex(newIndex);
        // Scroll to the selected row
        const rows = document.querySelectorAll('tbody tr');
        if (rows[newIndex]) {
          rows[newIndex].scrollIntoView({ block: 'nearest' });
          rows[newIndex].classList.add('Mui-selected');
          if (newIndex > 0 && rows[newIndex - 1]) {
            rows[newIndex - 1].classList.remove('Mui-selected');
          }
        }
      }
    },
    onPrevTicket: () => {
      if (viewMode === 'list' && tickets.length > 0) {
        const newIndex = Math.max(selectedTicketIndex - 1, 0);
        setSelectedTicketIndex(newIndex);
        // Scroll to the selected row
        const rows = document.querySelectorAll('tbody tr');
        if (rows[newIndex]) {
          rows[newIndex].scrollIntoView({ block: 'nearest' });
          rows[newIndex].classList.add('Mui-selected');
          if (newIndex < tickets.length - 1 && rows[newIndex + 1]) {
            rows[newIndex + 1].classList.remove('Mui-selected');
          }
        }
      }
    },
    onSelectTicket: () => {
      if (viewMode === 'list' && selectedTicketIndex >= 0 && tickets[selectedTicketIndex]) {
        handleViewTicket(tickets[selectedTicketIndex]);
      }
    }
  }, viewMode === 'list' || viewMode === 'view');

  // Handle URL params on mount
  useEffect(() => {
    const selectedId = searchParams.get('selectedId');
    const mode = searchParams.get('mode');

    if (selectedId) {
      // Fetch the selected ticket
      const fetchSelectedTicket = async () => {
        try {
          const response = await ticketsApi.getById(selectedId);
          const ticketData = response.data?.data?.ticket || response.data?.ticket || response.data;
          if (ticketData) {
            const transformed = transformTicketData(ticketData);
            setSelectedTicket(transformed);
            setViewMode(mode === 'edit' ? 'edit' : 'view');
          }
        } catch (err) {
          console.error('Error fetching ticket:', err);
          enqueueSnackbar(t ? t('Tickets.error_loading') : 'Failed to load ticket', { variant: 'error' });
          setViewMode('list');
        }
      };
      fetchSelectedTicket();
    }
  }, [searchParams, t, enqueueSnackbar]);

  // Navigation handlers
  const handleViewTicket = useCallback(async (ticket) => {
    try {
      // Fetch full ticket details
      const response = await ticketsApi.getById(ticket.id);
      const ticketData = response.data?.data?.ticket || response.data?.ticket || response.data;
      const transformed = transformTicketData(ticketData);
      setSelectedTicket(transformed);
      setViewMode('view');
      setSearchParams({ selectedId: ticket.id });
    } catch (err) {
      console.error('Error fetching ticket:', err);
      enqueueSnackbar(t ? t('Tickets.error_loading') : 'Failed to load ticket', { variant: 'error' });
    }
  }, [setSearchParams, enqueueSnackbar, t]);

  const handleEditTicket = useCallback(async (ticket) => {
    try {
      const response = await ticketsApi.getById(ticket.id);
      const ticketData = response.data?.data?.ticket || response.data?.ticket || response.data;
      const transformed = transformTicketData(ticketData);
      setSelectedTicket(transformed);
      setViewMode('edit');
      setSearchParams({ selectedId: ticket.id, mode: 'edit' });
    } catch (err) {
      console.error('Error fetching ticket:', err);
      enqueueSnackbar(t ? t('Tickets.error_loading') : 'Failed to load ticket', { variant: 'error' });
    }
  }, [setSearchParams, enqueueSnackbar, t]);

  const handleCreateTicket = useCallback(() => {
    setSelectedTicket(null);
    setViewMode('create');
    setSearchParams({});
  }, [setSearchParams]);

  const handleBackToList = useCallback(() => {
    setSelectedTicket(null);
    setViewMode('list');
    setSearchParams({});
  }, [setSearchParams]);

  const handleRefresh = useCallback(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Helper function to find the parent site for a location
  const findSiteForLocation = useCallback((locationId) => {
    if (!locationId || !locations || locations.length === 0) return null;

    const findInTree = (nodes, targetId) => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findInTree(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    // Find the location
    const location = findInTree(locations, locationId);
    if (!location) return null;

    // Walk up to find the site (highest level with parent = null or root)
    const findSite = (loc) => {
      if (!loc.parentId) return loc;
      const parent = findInTree(locations, loc.parentId);
      if (parent) return findSite(parent);
      return loc;
    };

    return findSite(location);
  }, [locations]);

  // PDF Export function - Card layout with CARO branding
  const handleExportPdf = useCallback(async () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const headerHeight = 25;
    const footerHeight = 15;
    const contentWidth = pageWidth - (2 * margin);
    const contentAreaTop = margin + headerHeight;
    const contentAreaBottom = pageHeight - footerHeight;
    let yPosition = contentAreaTop;

    // Brand colors (matching CARO style)
    const plumMain = [154, 63, 105];
    const grey800 = [28, 37, 46];
    const grey500 = [145, 158, 171];
    const grey300 = [223, 227, 232];
    const cardBg = [250, 250, 252];

    // Helper function to add header to current page
    const addHeader = async () => {
      pdf.setDrawColor(...plumMain);
      pdf.setLineWidth(0.5);
      pdf.line(margin, margin + 8, pageWidth - margin, margin + 8);

      try {
        const logoImg = new Image();
        logoImg.src = '/logo/logo-full.png';

        if (!logoImg.complete) {
          await new Promise((resolve, reject) => {
            logoImg.onload = resolve;
            logoImg.onerror = () => {
              pdf.setFontSize(11);
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(...plumMain);
              pdf.text('CARO Technology', margin, margin + 5);
              resolve();
            };
            setTimeout(reject, 1000);
          });
        }

        if (logoImg.complete && logoImg.naturalHeight !== 0) {
          const logoHeight = 6;
          const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
          pdf.addImage(logoImg, 'PNG', margin, margin, logoWidth, logoHeight);
        } else {
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...plumMain);
          pdf.text('CARO Technology', margin, margin + 5);
        }
      } catch (err) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...plumMain);
        pdf.text('CARO Technology', margin, margin + 5);
      }
    };

    // Helper function to add footer to current page
    const addFooter = (pageNum, totalPages) => {
      const footerY = pageHeight - footerHeight + 5;
      pdf.setDrawColor(...grey300);
      pdf.setLineWidth(0.3);
      pdf.line(margin, footerY, pageWidth - margin, footerY);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...grey500);
      pdf.text(`© ${new Date().getFullYear()} CARO Technology`, margin, footerY + 5);
      pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin - 25, footerY + 5);
    };

    // Helper function to check page break
    const checkPageBreak = async (requiredSpace = 10) => {
      if (yPosition + requiredSpace > contentAreaBottom) {
        pdf.addPage();
        await addHeader();
        yPosition = contentAreaTop;
        return true;
      }
      return false;
    };

    // Add header to first page
    await addHeader();

    // Document title section
    pdf.setTextColor(...plumMain);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Tickets To-Do List', margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...grey500);
    pdf.text('CARO Technology - Ticket Management', margin, yPosition);
    yPosition += 5;

    pdf.setFontSize(8);
    pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPosition);
    yPosition += 3;

    // Divider line
    pdf.setFillColor(...plumMain);
    pdf.rect(margin, yPosition, contentWidth, 0.5, 'F');
    yPosition += 8;

    // Filter info & count
    pdf.setFontSize(9);
    pdf.setTextColor(...grey800);
    const activeFilters = [];
    if (filter.status.length > 0) activeFilters.push(`Status: ${filter.status.join(', ')}`);
    if (filter.urgency) activeFilters.push(`Urgency: ${filter.urgency}`);
    if (filter.support_type) activeFilters.push(`Type: ${filter.support_type}`);
    if (filter.search) activeFilters.push(`Search: ${filter.search}`);

    if (activeFilters.length > 0) {
      pdf.setFont('helvetica', 'italic');
      pdf.text(`Filters: ${activeFilters.join(' | ')}`, margin, yPosition);
      yPosition += 5;
    }

    pdf.setFont('helvetica', 'bold');
    pdf.text(`Total: ${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`, margin, yPosition);
    yPosition += 10;

    // Draw each ticket as a card
    for (const ticket of tickets) {
      const locationId = ticket.locationId || ticket.location?.id;
      const site = findSiteForLocation(locationId);
      const siteName = site?.friendlyName || site?.name || '-';
      // Look up location name from locations array (more reliable than ticket.location nested object)
      const matchedLocation = locationId ? locations.find(l => String(l.id) === String(locationId) || String(l._id) === String(locationId)) : null;
      const locationName = matchedLocation?.friendlyName || matchedLocation?.name || ticket.location?.friendlyName || ticket.location?.name || '-';
      const status = ticket.status || '-';
      const urgency = ticket.urgency || '-';
      const title = ticket.title || 'No title';
      const description = ticket.description || '';

      // Calculate card height based on content
      pdf.setFontSize(8);
      const descriptionLines = description ? pdf.splitTextToSize(description, contentWidth - 10) : [];
      const cardHeaderHeight = 28;
      const descriptionHeight = descriptionLines.length > 0 ? (descriptionLines.length * 4) + 8 : 0;
      const cardHeight = cardHeaderHeight + descriptionHeight + 4;

      await checkPageBreak(cardHeight + 5);

      // Card background
      pdf.setFillColor(...cardBg);
      pdf.setDrawColor(...grey300);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(margin, yPosition, contentWidth, cardHeight, 2, 2, 'FD');

      // Card header background
      pdf.setFillColor(245, 243, 244);
      pdf.rect(margin + 0.15, yPosition + 0.15, contentWidth - 0.3, cardHeaderHeight - 0.15, 'F');

      // Header separator line
      pdf.setDrawColor(...grey300);
      pdf.line(margin, yPosition + cardHeaderHeight, margin + contentWidth, yPosition + cardHeaderHeight);

      // Ticket ID (plum color)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...plumMain);
      pdf.text(`#${ticket.id}`, margin + 4, yPosition + 6);

      // Title
      pdf.setTextColor(...grey800);
      const titleX = margin + 18;
      const titleText = title.length > 55 ? title.substring(0, 52) + '...' : title;
      pdf.text(titleText, titleX, yPosition + 6);

      // Row 2: Site
      pdf.setFontSize(8);
      const metaY1 = yPosition + 13;
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Site:', margin + 4, metaY1);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...plumMain);
      const siteText = siteName.length > 70 ? siteName.substring(0, 67) + '...' : siteName;
      pdf.text(siteText, margin + 14, metaY1);

      // Row 3: Location | Status | Urgency
      const metaY2 = yPosition + 20;
      let metaX = margin + 4;

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Location:', metaX, metaY2);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...grey800);
      metaX += 16;
      const locText = locationName.length > 25 ? locationName.substring(0, 22) + '...' : locationName;
      pdf.text(locText, metaX, metaY2);
      metaX += pdf.getTextWidth(locText) + 8;

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Status:', metaX, metaY2);
      metaX += 12;
      if (status === 'Open') pdf.setTextColor(25, 118, 210);
      else if (status === 'In Progress') pdf.setTextColor(237, 108, 2);
      else if (status === 'Resolved' || status === 'Closed') pdf.setTextColor(46, 125, 50);
      else pdf.setTextColor(...grey800);
      pdf.setFont('helvetica', 'normal');
      pdf.text(status, metaX, metaY2);
      metaX += pdf.getTextWidth(status) + 8;

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...grey500);
      pdf.text('Urgency:', metaX, metaY2);
      metaX += 14;
      if (urgency === 'HIGH') pdf.setTextColor(211, 47, 47);
      else if (urgency === 'MEDIUM') pdf.setTextColor(237, 108, 2);
      else if (urgency === 'LOW') pdf.setTextColor(25, 118, 210);
      else pdf.setTextColor(...grey800);
      pdf.setFont('helvetica', 'normal');
      pdf.text(urgency, metaX, metaY2);

      // Description
      if (descriptionLines.length > 0) {
        pdf.setTextColor(...grey800);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        let descY = yPosition + cardHeaderHeight + 6;
        descriptionLines.forEach((line) => {
          pdf.text(line, margin + 5, descY);
          descY += 4;
        });
      }

      yPosition += cardHeight + 5;
    }

    // Add footers to all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      addFooter(i, totalPages);
    }

    // Save the PDF
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const searchTerm = filter.search ? ` ${filter.search.replace(/[^a-zA-Z0-9\s-]/g, '')}` : '';
    const fileName = `${dateStr} Caro Ticket To do List${searchTerm}.pdf`;
    pdf.save(fileName);

    enqueueSnackbar(t ? t('Tickets.pdf_exported') : 'PDF exported successfully', { variant: 'success' });
  }, [tickets, filter, locations, findSiteForLocation, enqueueSnackbar, t]);

  // Handle ticket creation success
  const handleCreateSuccess = useCallback((newTicket) => {
    enqueueSnackbar(t ? t('Tickets.created_success') : 'Ticket created successfully', { variant: 'success' });
    fetchTickets();
    if (newTicket?.id) {
      handleViewTicket(newTicket);
    } else {
      handleBackToList();
    }
  }, [fetchTickets, handleViewTicket, handleBackToList, enqueueSnackbar, t]);

  const handleCreateClose = useCallback(() => {
    handleBackToList();
  }, [handleBackToList]);

  // Render based on view mode
  const renderContent = () => {
    switch (viewMode) {
      case 'create':
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">
                {t ? t('Tickets.create_ticket') : 'Create Ticket'}
              </Typography>
            </Box>
            <TicketCreateView
              onSuccess={handleCreateSuccess}
              onClose={handleCreateClose}
              embedded={true}
            />
          </Box>
        );

      case 'view':
        if (selectedTicket) {
          return (
            <TicketDetailView
              ticket={selectedTicket}
              loading={!selectedTicket}
              onBack={handleBackToList}
              onEdit={() => handleEditTicket(selectedTicket)}
              onDelete={async () => {
                try {
                  await ticketsApi.delete(selectedTicket.id);
                  enqueueSnackbar(t ? t('Tickets.deleted_success') : 'Ticket deleted', { variant: 'success' });
                  fetchTickets();
                  handleBackToList();
                } catch (err) {
                  enqueueSnackbar(t ? t('Tickets.delete_failed') : 'Failed to delete ticket', { variant: 'error' });
                }
              }}
              onUpdate={(updatedTicket) => {
                setSelectedTicket(updatedTicket);
                fetchTickets();
              }}
            />
          );
        }
        return null;

      case 'edit':
        // Edit mode uses the same detail view - editing is handled inline in the sidebar
        if (selectedTicket) {
          return (
            <TicketDetailView
              ticket={selectedTicket}
              loading={!selectedTicket}
              onBack={handleBackToList}
              onEdit={() => handleEditTicket(selectedTicket)}
              onDelete={async () => {
                try {
                  await ticketsApi.delete(selectedTicket.id);
                  enqueueSnackbar(t ? t('Tickets.deleted_success') : 'Ticket deleted', { variant: 'success' });
                  fetchTickets();
                  handleBackToList();
                } catch (err) {
                  enqueueSnackbar(t ? t('Tickets.delete_failed') : 'Failed to delete ticket', { variant: 'error' });
                }
              }}
              onUpdate={(updatedTicket) => {
                setSelectedTicket(updatedTicket);
                fetchTickets();
              }}
            />
          );
        }
        return null;

      case 'list':
      default:
        return (
          <TicketListView
            tickets={tickets}
            totalTickets={totalTickets}
            loading={loading}
            error={error}
            filter={filter}
            page={page}
            rowsPerPage={rowsPerPage}
            orderBy={orderBy}
            order={order}
            searchInputValue={searchInputValue}
            hasActiveFilters={hasActiveFilters}
            filterDrawerOpen={filterDrawerOpen}
            locations={locations}
            onFilterDrawerToggle={() => setFilterDrawerOpen(prev => !prev)}
            onFilterChange={handleFilterChange}
            onResetFilter={handleResetFilter}
            onSearchChange={handleSearchInputChange}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onSort={handleRequestSort}
            onViewTicket={handleViewTicket}
            onEditTicket={handleEditTicket}
            onCreateTicket={handleCreateTicket}
            onRefresh={handleRefresh}
            onExportPdf={handleExportPdf}
          />
        );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {viewMode === 'list' && (
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t ? t('Tickets.title') : 'Tickets'}
        </Typography>
      )}
      {renderContent()}

      {/* New data notification */}
      <Snackbar
        open={hasNewData}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={acceptNewData}>
                {t ? t('Common.refresh') : 'Refresh'}
              </Button>
              <Button color="inherit" size="small" onClick={dismissNewData}>
                {t ? t('Common.dismiss') : 'Dismiss'}
              </Button>
            </Box>
          }
        >
          {t ? t('Tickets.new_data_available') : 'New tickets available'}
        </Alert>
      </Snackbar>

      {/* Keyboard shortcuts dialog */}
      <ShortcutsDialog
        open={shortcutsDialogOpen}
        onClose={() => setShortcutsDialogOpen(false)}
      />
    </Container>
  );
};

export default TicketsManagement;
